import { useLocation, useNavigate } from "react-router-dom";
import { useCallback, useEffect } from "react";
import removeEmptyFields from "@/utilize/removeEmptyFields.ts";
import deepEqual from "deep-equal";
import qs, { IParseBaseOptions } from "qs";
// import { create } from "zustand";
// import { immer } from "zustand/middleware/immer";

const parsePrimitiveValues = (
  str: string,
  defaultDecoder: qs.defaultDecoder,
  charset: string,
  type: "key" | "value",
) => {
  if (
    type === "value" &&
    /^(?:-(?:[1-9](?:\d{0,2}(?:,\d{3})+|\d*))|(?:0|(?:[1-9](?:\d{0,2}(?:,\d{3})+|\d*))))(?:.\d+|)$/.test(
      str,
    )
  ) {
    return parseFloat(str);
  }

  const keywords = {
    true: true,
    false: false,
    null: null,
    undefined: undefined,
  };
  if (type === "value" && str in keywords) {
    return keywords[str as keyof typeof keywords];
  }

  return defaultDecoder(str, defaultDecoder, charset);
};

export const useQueryChangeEffect = (callback: () => any, deps: any[] = []) => {
  const { key } = useLocation();
  useEffect(callback, [key, ...deps]);
};

type TUseQueryParamsResponse<T> = {
  params: T;
  setParams: (params: T) => void;
  setParam: (key: keyof T, value: T[keyof T]) => void;
  deleteParam: (key: keyof T) => void;
  resetParams: () => void;
};

// type TStoreState<T = Record<string, any>> = {
//   defaultValue: T | Record<string, never> | undefined;
//   setDefaultValue: (defaultValue: TStoreState<T>["defaultValue"]) => void;
// };

// const useStoreRaw = create<TStoreState>()(
//   immer((set) => ({
//     defaultValue: {},
//     setDefaultValue: (defaultValue: TStoreState["defaultValue"]) =>
//       set((state) => {
//         state.defaultValue = defaultValue;
//       }),
//   })),
// );

// const useStore = useStoreRaw as {
//   <T>(): TStoreState<T>;
//   <T, U>(selector: (s: TStoreState<T>) => U): U;
// };

export const useQueryParams = <T>(
  defaultValue?: (() => T) | T | undefined,
  options: IParseBaseOptions = {},
): TUseQueryParamsResponse<T> => {
  const navigate = useNavigate();
  const location = useLocation();
  // const store = useStore<T>();

  // useEffect(() => {
  //     if (defaultValue) store.setDefaultValue(defaultValue);
  //     return () => {
  //         store.setDefaultValue({});
  //     };
  // }, []);

  const defaultVal = (defaultValue || (defaultValue as () => T)?.() || {}) as T;

  const getQueries = useCallback((search: string) => {
    return (qs.parse(search, {
      decoder: parsePrimitiveValues,
      ...options,
      ignoreQueryPrefix: true,
    }) || {}) as TUseQueryParamsResponse<T>["params"];
  }, []);

  const getParams = () => {
    const queries = getQueries(location.search || "");
    const removedEmpty = removeEmptyFields(queries);
    return { ...defaultVal, ...removedEmpty };
  };

  const params = getParams();
  const setParams = (newParam: T) => {
    newParam = removeEmptyFields(newParam);
    if (deepEqual(newParam, params)) return;
    const query = qs.stringify(newParam as any, {
      arrayFormat: "brackets",
    });
    navigate({ search: `?${query}` });
  };

  const setParam = (key: keyof T, value: T[keyof T]) => {
    setParams({ ...params, [key]: value });
  };

  const deleteParam = (key: keyof T) => {
    const params = getQueries(window.location.search);
    delete params[key];
    setParams(params);
  };

  const resetParams = () => {
    setParams(defaultVal);
  };

  return {
    params: JSON.parse(JSON.stringify(params)),
    setParams,
    setParam,
    deleteParam,
    resetParams,
  };
};
